import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import axios from "axios";
import {
  Tag, Plus, Trash2, Pencil, ExternalLink, Star, Loader2,
  ShoppingBag, Link2, Sparkles, Search, Globe, BarChart3,
  MousePointer, TrendingUp, Upload, ImageIcon, FolderOpen
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AffiliateProducts() {
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiUrl, setAiUrl] = useState("");
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const [mediaItems, setMediaItems] = useState([]);
  const [form, setForm] = useState(getEmptyForm());

  function getEmptyForm() {
    return {
      name: "", description: "", url: "", image_url: "", category: "Finanse",
      tags: "", priority: 50, status: "active", cta_text: "Sprawdz oferte",
      commission: "", commission_type: "percent", auto_insert: true,
      product_type: "", interest_rate: "", rrso: "", rating: "",
      min_amount: "", max_amount: "", min_period: "", max_period: "",
      features: "", requirements: "",
    };
  }

  const fetchData = useCallback(async () => {
    try {
      const [prodRes, statsRes] = await Promise.all([
        axios.get(`${API}/affiliates`),
        axios.get(`${API}/affiliates/stats`),
      ]);
      setProducts(prodRes.data);
      setStats(statsRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Load media library
  const loadMedia = async () => {
    try {
      const res = await axios.get(`${API}/media`);
      setMediaItems(res.data);
    } catch (err) { console.error(err); }
  };

  // Handle file upload from computer
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setForm(f => ({ ...f, image_url: ev.target.result, image_filename: file.name }));
    };
    reader.readAsDataURL(file);
  };

  // AI auto-fill z URL
  const handleAiFill = async () => {
    if (!aiUrl.trim()) { toast.error("Wklej link do produktu"); return; }
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/affiliates/ai-fill`, { url: aiUrl.trim() });
      const d = res.data;
      setForm({
        name: d.name || "", description: d.description || "",
        url: d.url || aiUrl, image_url: d.image_url || "",
        category: (d.categories || [])[0] || "Finanse",
        tags: (d.tags || []).join(", "), priority: 50,
        status: "active", cta_text: d.cta_text || "Sprawdz oferte",
        commission: "", commission_type: "percent", auto_insert: true,
        product_type: d.product_type || "",
        interest_rate: d.interest_rate || "", rrso: d.rrso || "",
        rating: d.rating || "", min_amount: d.min_amount || "",
        max_amount: d.max_amount || "", min_period: d.min_period || "",
        max_period: d.max_period || "",
        features: (d.features || []).join(", "),
        requirements: (d.requirements || []).join(", "),
      });
      toast.success("AI uzupelnil dane produktu");
    } catch (err) { toast.error(err.response?.data?.detail || "Blad AI"); }
    finally { setAiLoading(false); }
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...form,
        tags: form.tags ? form.tags.split(",").map(t2 => t2.trim()).filter(Boolean) : [],
        features: form.features ? form.features.split(",").map(f => f.trim()).filter(Boolean) : [],
        requirements: form.requirements ? form.requirements.split(",").map(r => r.trim()).filter(Boolean) : [],
        rating: form.rating ? parseFloat(form.rating) : null,
        min_amount: form.min_amount ? parseFloat(form.min_amount) : null,
        max_amount: form.max_amount ? parseFloat(form.max_amount) : null,
        min_period: form.min_period ? parseInt(form.min_period) : null,
        max_period: form.max_period ? parseInt(form.max_period) : null,
      };
      if (editing) {
        await axios.put(`${API}/affiliates/${editing.id}`, payload);
      } else {
        await axios.post(`${API}/affiliates`, payload);
      }
      toast.success(t("common.success"));
      fetchData(); setDialogOpen(false); setForm(getEmptyForm()); setEditing(null); setAiUrl("");
    } catch (err) { toast.error(t("common.error")); }
  };

  const handleEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, description: p.description || "", url: p.url,
      image_url: p.image_url || "", category: p.category,
      tags: (p.tags || []).join(", "), priority: p.priority || 50,
      status: p.status, cta_text: p.cta_text || "Sprawdz oferte",
      commission: p.commission || "", commission_type: p.commission_type || "percent",
      auto_insert: p.auto_insert ?? true,
      product_type: p.product_type || "", interest_rate: p.interest_rate || "",
      rrso: p.rrso || "", rating: p.rating || "",
      min_amount: p.min_amount || "", max_amount: p.max_amount || "",
      min_period: p.min_period || "", max_period: p.max_period || "",
      features: (p.features || []).join(", "), requirements: (p.requirements || []).join(", "),
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try { await axios.delete(`${API}/affiliates/${id}`); toast.success(t("common.success")); fetchData(); }
    catch (err) { toast.error(t("common.error")); }
  };

  // AI refresh na istniejacym produkcie
  const handleAiRefresh = async (product) => {
    try {
      const res = await axios.post(`${API}/affiliates/ai-fill`, { url: product.url });
      // Update
      const d = res.data;
      await axios.put(`${API}/affiliates/${product.id}`, {
        ...product, description: d.description || product.description,
        tags: d.tags || product.tags, features: d.features || product.features,
        requirements: d.requirements || product.requirements,
      });
      toast.success("Dane odswiezone przez AI");
      fetchData();
    } catch (err) { toast.error("Blad AI"); }
  };

  return (
    <div data-testid="affiliates-page" className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">Produkty Afiliacyjne</h1>
          <p className="text-sm text-muted-foreground mt-1 font-body">Podaj nazwe lub link, a AI automatycznie uzupelni pozostale pola</p>
        </div>
        <Button data-testid="add-product-btn" className="rounded-lg gap-2"
          onClick={() => { setDialogOpen(true); setEditing(null); setForm(getEmptyForm()); setAiUrl(""); }}>
          <Plus className="w-4 h-4" strokeWidth={1.5} /> Dodaj produkt
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-3">
          <Card className="rounded-xl border shadow-sm bg-card">
            <CardContent className="p-3 flex items-center gap-2">
              <Tag className="w-4 h-4 text-primary" />
              <div><p className="text-lg font-bold">{stats.active}</p><p className="text-[9px] text-muted-foreground">Produkty aktywne</p></div>
            </CardContent>
          </Card>
          <Card className="rounded-xl border shadow-sm bg-card">
            <CardContent className="p-3 flex items-center gap-2">
              <MousePointer className="w-4 h-4 text-blue-500" />
              <div><p className="text-lg font-bold">0</p><p className="text-[9px] text-muted-foreground">Klikniecia</p></div>
            </CardContent>
          </Card>
          <Card className="rounded-xl border shadow-sm bg-card">
            <CardContent className="p-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-500" />
              <div><p className="text-lg font-bold">0</p><p className="text-[9px] text-muted-foreground">Konwersje</p></div>
            </CardContent>
          </Card>
          <Card className="rounded-xl border shadow-sm bg-card">
            <CardContent className="p-3 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-amber-500" />
              <div><p className="text-lg font-bold">0%</p><p className="text-[9px] text-muted-foreground">Wspolczynnik</p></div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Lista produktow */}
      {products.length === 0 ? (
        <Card className="rounded-xl border shadow-sm bg-card">
          <CardContent className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <Tag className="w-10 h-10 mb-3 opacity-20" strokeWidth={1} />
            <p className="text-xs">Brak produktow afiliacyjnych</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {products.map((p) => (
            <Card key={p.id} data-testid={`product-${p.id}`} className="rounded-xl border shadow-sm bg-card hover:shadow-md transition-all">
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {/* Logo */}
                  <div className="w-16 h-16 rounded-lg bg-muted/50 border flex items-center justify-center shrink-0 overflow-hidden">
                    {p.image_url ? (
                      <img src={p.image_url} alt={p.name} className="w-full h-full object-contain" />
                    ) : (
                      <Globe className="w-6 h-6 text-muted-foreground" strokeWidth={1} />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-semibold text-foreground">{p.name}</h3>
                      {p.rating && (
                        <div className="flex items-center gap-0.5">
                          <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                          <span className="text-xs font-bold text-amber-500">{p.rating}</span>
                        </div>
                      )}
                      <Badge className={`text-[9px] border-0 rounded-md ${p.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                        {p.status === "active" ? "Aktywny" : "Nieaktywny"}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{p.description}</p>
                    <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-primary hover:underline flex items-center gap-0.5 mb-2">
                      <Link2 className="w-2.5 h-2.5" /> {p.url?.substring(0, 60)}...
                    </a>
                    <div className="flex flex-wrap gap-1">
                      {p.product_type && <Badge variant="outline" className="text-[8px] rounded-md">{p.product_type}</Badge>}
                      {p.category && <Badge variant="secondary" className="text-[8px] rounded-md">{p.category}</Badge>}
                      {(p.tags || []).slice(0, 4).map((tag, i) => (
                        <Badge key={i} variant="outline" className="text-[8px] rounded-md">{tag}</Badge>
                      ))}
                    </div>
                    {/* Szczegoly finansowe */}
                    {(p.interest_rate || p.rrso || p.min_amount) && (
                      <div className="flex items-center gap-3 mt-2 text-[10px] text-muted-foreground">
                        {p.interest_rate && <span>Opr: {p.interest_rate}</span>}
                        {p.rrso && <span>RRSO: {p.rrso}</span>}
                        {p.min_amount && p.max_amount && <span>{p.min_amount}-{p.max_amount} PLN</span>}
                      </div>
                    )}
                  </div>

                  {/* Akcje */}
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <Button size="sm" className="h-7 text-[10px] gap-1 rounded-md" asChild>
                      <a href={p.url} target="_blank" rel="noopener noreferrer">
                        {p.cta_text || "Sprawdz oferte"}
                      </a>
                    </Button>
                    <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1" onClick={() => handleAiRefresh(p)}>
                      <Sparkles className="w-3 h-3" /> Pobierz AI
                    </Button>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEdit(p)}>
                        <Pencil className="w-3 h-3" strokeWidth={1.5} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(p.id)}>
                        <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                      </Button>
                    </div>
                    <p className="text-[8px] text-muted-foreground text-center">P: {p.priority}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ===== DIALOG DODAWANIA/EDYCJI ===== */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) { setEditing(null); setForm(getEmptyForm()); }}}>
        <DialogContent className="sm:max-w-none max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edytuj produkt" : "Dodaj produkt afiliacyjny"}</DialogTitle>
          </DialogHeader>

          {/* AI Auto-fill */}
          {!editing && (
            <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 space-y-2">
              <p className="text-xs font-semibold text-primary">Podaj link, a AI automatycznie uzupelni pola</p>
              <div className="flex gap-2">
                <Input value={aiUrl} onChange={(e) => setAiUrl(e.target.value)}
                  placeholder="https://www.example.com/produkt" className="flex-1 text-xs" />
                <Button onClick={handleAiFill} disabled={aiLoading} className="gap-1 shrink-0">
                  {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  {aiLoading ? "Analizuję..." : "AI Uzupelnij"}
                </Button>
              </div>
            </div>
          )}

          <div className="grid gap-3 py-2">
            {/* Podstawowe */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Nazwa produktu</Label>
                <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Link afiliacyjny</Label>
                <Input value={form.url} onChange={(e) => setForm({...form, url: e.target.value})} placeholder="https://..." />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Opis (unikalny)</Label>
              <Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={3} className="resize-none text-xs" />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Tekst CTA</Label>
                <Input value={form.cta_text} onChange={(e) => setForm({...form, cta_text: e.target.value})} />
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label className="text-xs">Logo / Zdjecie produktu</Label>
                <div className="flex gap-2">
                  {/* Podglad */}
                  <div className="w-14 h-14 rounded-lg border bg-muted/50 flex items-center justify-center shrink-0 overflow-hidden">
                    {form.image_url ? (
                      <img src={form.image_url} alt="" className="w-full h-full object-contain" />
                    ) : (
                      <ImageIcon className="w-5 h-5 text-muted-foreground" strokeWidth={1} />
                    )}
                  </div>
                  <div className="flex-1 space-y-1.5">
                    {/* URL reczny */}
                    <Input value={form.image_url?.startsWith("data:") ? "(plik z komputera)" : form.image_url || ""}
                      onChange={(e) => setForm({...form, image_url: e.target.value})}
                      placeholder="URL obrazka lub wybierz ponizej" className="h-7 text-[10px]" />
                    <div className="flex gap-1.5">
                      {/* Upload z komputera */}
                      <label className="flex items-center gap-1 px-2 py-1 rounded-md border text-[10px] text-muted-foreground hover:bg-accent cursor-pointer transition-colors">
                        <Upload className="w-3 h-3" /> Z komputera
                        <input type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
                      </label>
                      {/* Z biblioteki mediow */}
                      <button onClick={() => { loadMedia(); setMediaPickerOpen(true); }}
                        className="flex items-center gap-1 px-2 py-1 rounded-md border text-[10px] text-muted-foreground hover:bg-accent transition-colors">
                        <FolderOpen className="w-3 h-3" /> Z biblioteki
                      </button>
                      {form.image_url && (
                        <button onClick={() => setForm({...form, image_url: ""})}
                          className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] text-destructive hover:bg-red-50 transition-colors">
                          <Trash2 className="w-3 h-3" /> Usun
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Kategoria</Label>
              <Input value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Tagi / Slowa kluczowe (przecinkami)</Label>
                <Input value={form.tags} onChange={(e) => setForm({...form, tags: e.target.value})} placeholder="pozyczka, finanse, kredyt..." />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Priorytet: {form.priority}</Label>
                <Slider value={[form.priority]} onValueChange={([v]) => setForm({...form, priority: v})} min={0} max={100} step={1} />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Typ prowizji</Label>
                <Select value={form.commission_type} onValueChange={(v) => setForm({...form, commission_type: v})}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent" className="text-xs">Procentowa (%)</SelectItem>
                    <SelectItem value="fixed" className="text-xs">Stala (PLN)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Prowizja</Label>
                <Input value={form.commission} onChange={(e) => setForm({...form, commission: e.target.value})} placeholder="10%" />
              </div>
              <div className="flex items-end pb-1">
                <div className="flex items-center gap-2">
                  <Switch checked={form.auto_insert} onCheckedChange={(v) => setForm({...form, auto_insert: v})} />
                  <Label className="text-xs">Aktywny</Label>
                </div>
              </div>
            </div>

            <Separator />
            <p className="text-xs font-semibold text-foreground">Szczegoly produktu (opcjonalne)</p>

            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[10px]">Typ produktu</Label>
                <Input value={form.product_type} onChange={(e) => setForm({...form, product_type: e.target.value})} placeholder="pozyczka" className="h-8 text-xs" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px]">Oprocentowanie</Label>
                <Input value={form.interest_rate} onChange={(e) => setForm({...form, interest_rate: e.target.value})} placeholder="7.2%" className="h-8 text-xs" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px]">RRSO</Label>
                <Input value={form.rrso} onChange={(e) => setForm({...form, rrso: e.target.value})} placeholder="10.5%" className="h-8 text-xs" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px]">Ocena (0-5)</Label>
                <Input type="number" value={form.rating} onChange={(e) => setForm({...form, rating: e.target.value})} placeholder="4.5" className="h-8 text-xs" />
              </div>
            </div>

            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[10px]">Min kwota (PLN)</Label>
                <Input type="number" value={form.min_amount} onChange={(e) => setForm({...form, min_amount: e.target.value})} className="h-8 text-xs" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px]">Max kwota (PLN)</Label>
                <Input type="number" value={form.max_amount} onChange={(e) => setForm({...form, max_amount: e.target.value})} className="h-8 text-xs" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px]">Min okres (mc)</Label>
                <Input type="number" value={form.min_period} onChange={(e) => setForm({...form, min_period: e.target.value})} className="h-8 text-xs" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px]">Max okres (mc)</Label>
                <Input type="number" value={form.max_period} onChange={(e) => setForm({...form, max_period: e.target.value})} className="h-8 text-xs" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[10px]">Cechy produktu (przecinkami)</Label>
                <Input value={form.features} onChange={(e) => setForm({...form, features: e.target.value})} placeholder="Szybka decyzja, Bez zaswiadczen..." className="text-xs" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px]">Wymagania (przecinkami)</Label>
                <Input value={form.requirements} onChange={(e) => setForm({...form, requirements: e.target.value})} placeholder="Wiek 18+, Dowod osobisty..." className="text-xs" />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t("sites.cancel")}</Button>
            <Button onClick={handleSave}>{t("sites.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== MEDIA PICKER DIALOG ===== */}
      <Dialog open={mediaPickerOpen} onOpenChange={setMediaPickerOpen}>
        <DialogContent className="sm:max-w-none max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="font-heading">Wybierz zdjecie z biblioteki</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[50vh]">
            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2 p-2">
              {mediaItems.map((img) => (
                <button key={img.id || img.filename}
                  onClick={() => {
                    setForm(f => ({ ...f, image_url: img.url || `/media/simulated/${img.filename}` }));
                    setMediaPickerOpen(false);
                    toast.success(`Wybrano: ${img.filename}`);
                  }}
                  className="rounded-lg border hover:border-primary hover:shadow-md transition-all overflow-hidden group">
                  <div className="aspect-square bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 flex items-center justify-center relative">
                    <ImageIcon className="w-6 h-6 text-slate-300 dark:text-slate-600" strokeWidth={1} />
                    {img.simulated && (
                      <span className="absolute top-0.5 right-0.5 text-[6px] bg-amber-100 text-amber-700 px-1 rounded">SIM</span>
                    )}
                  </div>
                  <div className="p-1">
                    <p className="text-[8px] font-medium text-foreground truncate">{img.filename}</p>
                    <p className="text-[7px] text-muted-foreground truncate">{img.category}</p>
                  </div>
                </button>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
